import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import userApi from '../../api/userApi';

const TrashedUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [userToActOnId, setUserToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedUsers = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await userApi.getUsers({ page, perPage, search, includeTrashed: true });
      setUsers(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch trashed Users:', err);
      setError('Failed to load trashed Users. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrashedUsers(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedUsers, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setUserToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (userToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await userApi.restoreUser(userToActOnId);
        await fetchTrashedUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore User:', err);
        setError('Failed to restore User. Please try again.');
      } finally {
        setLoading(false);
        setUserToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setUserToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (userToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await userApi.permanentDeleteUser(userToActOnId);
        await fetchTrashedUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete User:', err);
        setError('Failed to permanently delete User. Please try again.');
      } finally {
        setLoading(false);
        setUserToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setUserToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed Users</h1>
        <Button
          onClick={() => navigate('/users')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to Users
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed Users"
          id="search-trashed-users"
          name="search-trashed-users"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by name or email..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Name</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Email</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Role</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{user.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{user.first_name} {user.last_name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{user.email}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{user.roles && user.roles.length > 0 ? user.roles.map(role => role.name).join(', ') : 'N/A'}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(user.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-user-tooltip-${user.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(user.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-user-tooltip-${user.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-sm text-text-gray">No trashed Users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this User?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this User? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedUsers;